import Entity from "../../core/entity/Entity";
import { V, V2d } from "../../core/Vector";
import { LevelEntity } from "../LevelEntity";
import { SpiralersEnemy } from "./SpiralersEnemy";

const SPIRALERS_PER_SWARM = 24;
const SPAWN_DELAY = 0.05; // Seconds between spawning each spiraler
const SPIRAL_LOOPS = 3; // Number of complete loops before reaching center
const SPIRAL_START_RADIUS = 300; // Starting distance from center
const SPIRAL_END_RADIUS = 10; // End very close to center for kamikaze

export class SpiralersSwarm extends LevelEntity implements Entity {
  tags = ["spiralersSwarm"];
  private spawnPosition: V2d;
  private spiralers: SpiralersEnemy[] = [];
  private waypoints: V2d[] = [];

  constructor(spawnPosition: V2d) {
    super();
    this.spawnPosition = spawnPosition.clone();
  }

  async onAdd(): Promise<void> {
    // Generate waypoints for the swarm's spiral path
    this.generateSpiralWaypoints();

    // Spawn all spiralers with delay
    for (let i = 0; i < SPIRALERS_PER_SWARM; i++) {
      this.spawnSpiraler();
      if (i < SPIRALERS_PER_SWARM - 1) {
        await this.wait(SPAWN_DELAY);
      }
    }

    // Monitor for all spiralers being destroyed
    await this.waitUntil(() => this.spiralers.every((s) => s.isDestroyed()));
    this.destroy();
  }

  private generateSpiralWaypoints(): void {
    // Generate a logarithmic spiral that contracts toward the center
    const numWaypoints = 100; // Many waypoints for smooth spiral

    // Calculate initial angle based on spawn position
    const initialAngle = Math.atan2(this.spawnPosition.y, this.spawnPosition.x);
    const initialRadius = this.spawnPosition.magnitude;

    for (let i = 0; i <= numWaypoints; i++) {
      const t = i / numWaypoints; // 0 to 1

      // Logarithmic spiral parameters
      // Start from current angle and add rotations
      const angle = initialAngle + t * Math.PI * 2 * SPIRAL_LOOPS;

      // Interpolate radius from spawn distance to center
      const radius =
        initialRadius *
        Math.exp(-t * Math.log(initialRadius / SPIRAL_END_RADIUS));

      // Convert polar to cartesian
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;

      this.waypoints.push(V(x, y));
    }
  }

  private spawnSpiraler(): void {
    if (!this.game) return;

    const spiraler = new SpiralersEnemy(this.spawnPosition, this.waypoints);
    this.spiralers.push(spiraler);

    // Add to level
    this.getCurrentLevel()?.addChild(spiraler);
  }
}
